import { Dispatch } from 'redux';
import { Store } from '@reduxjs/toolkit';

import * as HrActions from '../actions/hr.action';
import { IService } from '../../shared/interfaces/service.interface';
import {
  IDashboardFetchApplicationDataAction,
  IDashboardFetchApprovalDataAction,
  IDashboardFetchStaticDataAction,
  IHrAction,
  IHrApprovalAddAction,
  IHrApprovalDeleteAction,
  IHrDeleteAction,
  IHrInformationAction,
  IHrModalInformationAction,
} from '../../shared/interfaces/action.interface';
import {
  IDashboardFetchApplicationResponse,
  IDashboardFetchApprovalResponse,
  IDashboardFetchStaticDataResponse,
  IHrApprovalResponse,
  IHrApprovalsAddResponse,
  IHrFetchInformationResponse,
  IHrFetchResponse,
} from '../../shared/interfaces/hr.interface';

const fetchHr =
  ({ log, api, toast }: IService) =>
  ({ dispatch }: Store) =>
  (next: Dispatch) =>
  async (action: IHrAction) => {
    next(action);
    const { type, payload } = action;

    if (type === HrActions.HR_FETCH_APPLICATIONS) {
      try {
        const { data, success, errors }: IHrFetchResponse = await api.hr.fetch(payload);

        if (success) {
          dispatch(HrActions.HrFetchApplicationsSuccess(data));
        } else {
          const [{ message }] = errors;

          toast('error', message);
          dispatch(HrActions.HrFetchApplicationsError());
        }
      } catch (error: any) {
        log(error);
      }
    }
  };

const fetchHrInformation =
  ({ log, api, toast }: IService) =>
  ({ dispatch, getState }: Store) =>
  (next: Dispatch) =>
  async (action: IHrInformationAction) => {
    next(action);
    const { type, payload } = action;

    if (type === HrActions.HR_FETCH_APPLICATION_INFORMATION) {
      try {
        const {
          hr: {
            selected: { application },
          },
        } = getState();

        if (!application || payload !== application.slug) {
          const { data, success, errors }: IHrFetchInformationResponse =
            await api.hr.fetchInformation(payload, null);

          if (success) {
            dispatch(HrActions.HrFetchApplicationSuccess(data));
          } else {
            const [{ message }] = errors;

            toast('error', message);
            dispatch(HrActions.HrFetchApplicationError());
          }
        }
      } catch (error: any) {
        log(error);
      }
    }
  };

const EditHrInformation =
  ({ log, api, toast }: IService) =>
  ({ dispatch, getState }: Store) =>
  (next: Dispatch) =>
  async (action: IHrModalInformationAction) => {
    next(action);
    const { type, slug, payload } = action;

    if (type === HrActions.HR_EDIT_APPLICATION_INFORMATION) {
      try {
        if (slug) {
          const { data, success, errors }: IHrFetchInformationResponse =
            await api.hr.editInformation(slug, payload);
          if (success) {
            toast('success', 'Information successfully updated.');
            dispatch(HrActions.HrEditApplicationSuccess(data));
          } else {
            const [{ message }] = errors;

            toast('error', message);
            dispatch(HrActions.HrEditApplicationError());
          }
        }
      } catch (error: any) {
        log(error);
      }
    }
  };

const deleteApplication =
  ({ toast, log, api }: IService) =>
  ({ dispatch, getState }: Store) =>
  (next: Dispatch) =>
  async (action: IHrDeleteAction) => {
    next(action);
    const { type, payload } = action;

    if (type === HrActions.HR_DELETE_APPLICATION) {
      try {
        const { success, errors } = await api.hr.delete(payload.slug, payload.remarks);

        if (success) {
          dispatch(HrActions.HrDeleteApplicationSuccess(payload.slug));
          toast('success', 'Application deleted successfully.');
        } else {
          const message = errors[0].message;

          dispatch(HrActions.HrDeleteApplicationError());
          toast('error', message);
        }
      } catch (error: any) {
        log(error);
      }
    }
  };

const addApproval =
  ({ log, api, toast }: IService) =>
  ({ dispatch, getState }: Store) =>
  (next: Dispatch) =>
  async (action: IHrApprovalAddAction) => {
    next(action);
    const { type, payload } = action;
    const {
      hr: { selected },
    } = getState();

    if (type === HrActions.HR_ADD_APPROVER) {
      try {
        const {
          /* eslint-disable camelcase */
          data: { signatory_remarks },
          success,
          errors,
        }: IHrApprovalsAddResponse = await api.hr.addApprover(null, payload);

        if (success) {
          if (signatory_remarks) dispatch(HrActions.HrAddApproverSuccess(signatory_remarks));

          toast('success', 'Application approval added successfully.');
        } else {
          const message = errors[0].message;

          dispatch(HrActions.HrAddApproverError());
          toast('error', message);
        }
      } catch (error: any) {
        log(error);
      }
    }
  };

const deleteApprover =
  ({ log, api, toast }: IService) =>
  ({ dispatch }: Store) =>
  (next: Dispatch) =>
  async (action: IHrApprovalDeleteAction) => {
    next(action);
    const { type, payload } = action;

    if (type === HrActions.HR_DELETE_APPROVER) {
      try {
        const { success, errors }: IHrApprovalResponse = await api.hr.deleteApprover(null, payload);
        if (success) {
          dispatch(HrActions.HrDeleteApproverSuccess(payload));
          toast('success', 'Application approval deleted successfully.');
        } else {
          const message = errors[0].message;

          dispatch(HrActions.HrDeleteApproverError());
          toast('error', message);
        }
      } catch (error: any) {
        log(error);
      }
    }
  };

const fetchStaticData =
  ({ log, api, toast }: IService) =>
  ({ dispatch }: Store) =>
  (next: Dispatch) =>
  async (action: IDashboardFetchStaticDataAction) => {
    next(action);
    const { type } = action;

    if (type === HrActions.DASHBOARD_FETCH_STATIC_DATA) {
      try {
        const { data, success, errors }: IDashboardFetchStaticDataResponse =
          await api.hr.fetchStaticDashboardData();
        if (success) {
          dispatch(HrActions.DashboardStaticDataSuccess(data));
        } else {
          const message = errors[0].message;

          dispatch(HrActions.DashboardStaticDataError());
          toast('error', message);
        }
      } catch (error: unknown) {
        log(error);
      }
    }
  };

const fetchApplicationData =
  ({ log, api, toast }: IService) =>
  ({ dispatch }: Store) =>
  (next: Dispatch) =>
  async (action: IDashboardFetchApplicationDataAction) => {
    next(action);
    const { type, payload } = action;

    if (type === HrActions.DASHBOARD_FETCH_APPLICATION_DATA) {
      try {
        const { data, success, errors }: IDashboardFetchApplicationResponse =
          await api.hr.fetchApplicationDashboardData(payload);

        if (success) {
          dispatch(HrActions.DashboardApplicationDataSuccess(data));
        } else {
          const [{ message }] = errors;

          toast('error', message);
          dispatch(HrActions.DashboardApplicationDataError());
        }
      } catch (error: unknown) {
        log(error);
      }
    }
  };

const fetchApprovalData =
  ({ log, api, toast }: IService) =>
  ({ dispatch }: Store) =>
  (next: Dispatch) =>
  async (action: IDashboardFetchApprovalDataAction) => {
    next(action);
    const { type, payload } = action;

    if (type === HrActions.DASHBOARD_FETCH_APPROVAL_DATA) {
      try {
        const { data, success, errors }: IDashboardFetchApprovalResponse =
          await api.hr.fetchApprovalDashboardData(payload);

        if (success) {
          dispatch(HrActions.DashboardApprovalDataSuccess(data));
        } else {
          const [{ message }] = errors;

          toast('error', message);
          dispatch(HrActions.DashboardApprovalDataError());
        }
      } catch (error: unknown) {
        log(error);
      }
    }
  };

const hr = [
  fetchHr,
  fetchHrInformation,
  EditHrInformation,
  deleteApplication,
  addApproval,
  deleteApprover,
  fetchStaticData,
  fetchApplicationData,
  fetchApprovalData,
];

export default hr;
